"use client";

import PublisherConfig from "@/configs/publisher.json";
import { getMediaFullPath } from "@/lib/media";
import { cn } from "@/lib/utils";
import * as React from "react";

const Avatar = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full bg-muted", className)} {...props} />
));
Avatar.displayName = "Avatar";

const AvatarImage = React.forwardRef<HTMLImageElement, React.ImgHTMLAttributes<HTMLImageElement>>(({ className, ...props }, ref) => (
  <img ref={ref} className={cn("aspect-square h-full w-full object-cover", className)} {...props} />
));
AvatarImage.displayName = "AvatarImage";

const AvatarFallback = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex h-full w-full items-center justify-center rounded-full bg-muted", className)} {...props} />
));
AvatarFallback.displayName = "AvatarFallback";

function firstLetter(value: string): string {
  const match = value.match(/\p{L}/u);
  return match ? match[0].toLocaleUpperCase() : "";
}

function getInitials(name: string): string {
  if (!name) return "";
  const parts = name.trim().split(/\s+/).map(firstLetter).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return parts[0] + parts[parts.length - 1];
}

function getAvatarBgColor(name: string): string {
  if (!name) return "bg-muted text-muted-foreground border border-border";
  const colors = [
    "bg-red-500/10 text-red-500 border border-red-500/20 dark:bg-red-500/15 dark:text-red-400",
    "bg-orange-500/10 text-orange-500 border border-orange-500/20 dark:bg-orange-500/15 dark:text-orange-400",
    "bg-amber-500/10 text-amber-500 border border-amber-500/20 dark:bg-amber-500/15 dark:text-amber-400",
    "bg-green-500/10 text-green-500 border border-green-500/20 dark:bg-green-500/15 dark:text-green-400",
    "bg-teal-500/10 text-teal-500 border border-teal-500/20 dark:bg-teal-500/15 dark:text-teal-400",
    "bg-blue-500/10 text-blue-500 border border-blue-500/20 dark:bg-blue-500/15 dark:text-blue-400",
    "bg-indigo-500/10 text-indigo-500 border border-indigo-500/20 dark:bg-indigo-500/15 dark:text-indigo-400",
    "bg-purple-500/10 text-purple-500 border border-purple-500/20 dark:bg-purple-500/15 dark:text-purple-400",
    "bg-pink-500/10 text-pink-500 border border-pink-500/20 dark:bg-pink-500/15 dark:text-pink-400",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
}

export interface UserAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  name?: string;
  colorSeed?: string;
  fallbackClassName?: string;
}

export const UserAvatar = React.memo(
  React.forwardRef<HTMLDivElement, UserAvatarProps>(({ src, name, colorSeed, className, fallbackClassName, ...props }, ref) => {
    const initials = React.useMemo(() => getInitials(name || ""), [name]);
    const colorClass = React.useMemo(() => getAvatarBgColor(colorSeed || name || ""), [colorSeed, name]);
    const [hasError, setHasError] = React.useState(false);

    React.useEffect(() => {
      setHasError(false);
    }, [src]);

    const showImage = Boolean(src && src.trim() !== "" && !hasError);

    return (
      <Avatar ref={ref} className={className} {...props}>
        <AvatarFallback
          className={cn(
            "absolute inset-0 flex h-full w-full items-center justify-center rounded-full font-bold select-none text-xs tracking-wider",
            colorClass,
            fallbackClassName,
            showImage ? "opacity-0 pointer-events-none" : "opacity-100",
          )}
        >
          {initials || "U"}
        </AvatarFallback>

        {showImage ? (
          <AvatarImage src={src} alt={name || "User avatar"} className="absolute inset-0" onError={() => setHasError(true)} />
        ) : null}
      </Avatar>
    );
  }),
);
UserAvatar.displayName = "UserAvatar";

const publisherAvatarSrc = PublisherConfig.profile.avatar ? getMediaFullPath(PublisherConfig.profile.avatar) : undefined;

export function PublisherAvatar({ className, fallbackClassName, ...props }: Omit<UserAvatarProps, "src" | "name" | "colorSeed">) {
  return (
    <UserAvatar
      src={publisherAvatarSrc}
      name={PublisherConfig.profile.name}
      colorSeed={PublisherConfig.username}
      className={className}
      fallbackClassName={fallbackClassName}
      {...props}
    />
  );
}
